import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TournamentDashboard.css';
import bishopGamesLogo from '../img/bishop_games_logo.png';

const TournamentDashboard = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();

  const TOURNAMENT_ID = '215f8af9-c786-4b42-8f14-adae8bef13f4';

  // State
  const [loading, setLoading] = useState(true);
  const [chartReady, setChartReady] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedSport, setSelectedSport] = useState('5F68C7E0-DA61-4142-94E6-B3F018C5255C'); // Badminton default
  const [teamSchedule, setTeamSchedule] = useState([]);
  const [allSchedule, setAllSchedule] = useState([]);
  const [overallLeaderboard, setOverallLeaderboard] = useState([]);
  const [sportLeaderboard, setSportLeaderboard] = useState([]);
  const [error, setError] = useState(null);

  // Sports data
  const sports = [
    { id: '5F68C7E0-DA61-4142-94E6-B3F018C5255C', code: 'BADMINTON', name: 'Badminton', scoringType: 'SET' },
    { id: '524FB7B9-D5CF-4291-B878-3A38F57C36FA', code: 'BOX_CRICKET', name: 'Box Cricket', scoringType: 'POINT' },
    { id: 'C22D70AC-F8BD-4916-924F-5D4E479AFA78', code: 'CARROM', name: 'Carrom', scoringType: 'SET' },
    { id: '490509BA-0AE1-46D3-8359-2194BDE803BC', code: 'CHESS', name: 'Chess', scoringType: 'RESULT' },
    { id: 'F0F86724-6315-4918-9C4F-4F4B8C7F230E', code: 'CROQUET', name: 'Croquet', scoringType: 'POINT' },
    { id: 'B382B4E3-004A-49B9-A6A5-9AA3CF1B2447', code: 'FUTSAL', name: 'Futsal', scoringType: 'POINT' },
    { id: '6DB11D9F-C504-4CB6-8A2A-B7B8C66EED25', code: 'SATODIYU', name: 'Satodiyu', scoringType: 'POINT' },
    { id: '7CD6C673-1965-486D-A12F-6FB286648373', code: 'TABLE_TENNIS', name: 'Table Tennis', scoringType: 'SET' },
    { id: 'B5C67FD9-80B3-4FBF-947D-541BDE90A8F7', code: 'TENNIS', name: 'Tennis', scoringType: 'SET' },
    { id: '97B9519C-D408-42C9-BA13-1E2DB680758B', code: 'VOLLEYBALL', name: 'Volleyball', scoringType: 'SET' },
    { id: '4455A1D4-F70E-4055-B9D4-43B9094F66A2', code: 'PICKLEBALL', name: 'Pickle Ball', scoringType: 'SET' }
  ];

  // Load Chart.js and check when ready
  useEffect(() => {
    const loadChartJS = () => {
      if (typeof window.Chart !== 'undefined') {
        console.log('Chart.js already loaded');
        setChartReady(true);
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="chart.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          console.log('Chart.js loaded from existing script');
          setChartReady(true);
        });
        return;
      }

      // Load Chart.js
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.async = true;
      script.onload = () => {
        console.log('Chart.js loaded successfully');
        setChartReady(true);
      };
      script.onerror = () => {
        console.error('Failed to load Chart.js');
      };
      document.head.appendChild(script);
    };

    loadChartJS();
  }, []);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('Token');
  };

  // Fetch all data on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch team schedule when team selected
  useEffect(() => {
    if (selectedTeam) {
      fetchTeamSchedule(selectedTeam);
    }
  }, [selectedTeam]);

  // Fetch sport leaderboard when sport selected
  useEffect(() => {
    if (selectedSport) {
      fetchSportLeaderboard(selectedSport);
    }
  }, [selectedSport]);

  // Initialize and update chart when sport leaderboard changes
  useEffect(() => {
    console.log('Sport chart effect triggered:', {
      hasData: sportLeaderboard.length > 0,
      chartReady,
      dataLength: sportLeaderboard.length,
      data: sportLeaderboard
    });

    if (sportLeaderboard.length > 0 && chartReady) {
      console.log('Attempting to render sport chart...');
      // Wait a bit longer for DOM to be fully ready
      const timer = setTimeout(() => {
        const canvas = document.getElementById('sportPointsChart');
        console.log('Sport canvas check:', canvas);
        if (canvas) {
          renderSportChart();
        } else {
          console.warn('Sport chart canvas still not in DOM');
        }
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [sportLeaderboard, chartReady, teams]);

  // Initialize and update overall chart when overall leaderboard changes
  useEffect(() => {
    console.log('Overall chart effect triggered:', {
      hasData: overallLeaderboard.length > 0,
      chartReady,
      dataLength: overallLeaderboard.length,
      data: overallLeaderboard
    });

    if (overallLeaderboard.length > 0 && chartReady) {
      console.log('Attempting to render overall chart...');
      // Wait a bit longer for DOM to be fully ready
      const timer = setTimeout(() => {
        const canvas = document.getElementById('overallPointsChart');
        console.log('Overall canvas check:', canvas);
        if (canvas) {
          renderOverallChart();
        } else {
          console.warn('Overall chart canvas still not in DOM');
        }
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [overallLeaderboard, chartReady, teams]);

  const renderOverallChart = () => {
    console.log('=== OVERALL CHART RENDER START ===');
    console.log('Chart.js available?', typeof window.Chart !== 'undefined');
    console.log('Data:', overallLeaderboard);

    if (typeof window.Chart === 'undefined') {
      console.error('Chart.js not loaded yet');
      return;
    }

    const ctx = document.getElementById('overallPointsChart');
    console.log('Canvas element:', ctx);

    if (!ctx) {
      console.warn('Overall chart canvas not yet available in DOM');
      return;
    }

    try {
      console.log('Creating overall chart...');

      // Destroy existing chart if it exists
      if (window.overallChartInstance) {
        console.log('Destroying previous overall chart instance');
        window.overallChartInstance.destroy();
      }

      // Prepare data
      const teams = overallLeaderboard.map(team => team.teamName || getTeamName(team.teamId));
      const points = overallLeaderboard.map(team => team.totalPoints || 0);

      console.log('Chart data prepared:');
      console.log('  Teams:', teams);
      console.log('  Points:', points);

      if (teams.length === 0 || points.length === 0) {
        console.error('No data to display in chart');
        return;
      }

      // Generate gradient colors - darker for higher points
      const maxPoints = Math.max(...points);
      console.log('  Max points:', maxPoints);

      // ✅ FIX: Generate 13 distinct pastel colors for all teams
      const pastelColors = [
        'rgba(253, 136, 154, 0.85)',  // 1. Light Pink
        'rgba(112, 223, 238, 0.85)',  // 2. Powder Blue
        'rgba(221, 160, 221, 0.85)',  // 3. Plum
        'rgba(250, 173, 106, 0.85)',  // 4. Peach
        'rgba(40, 135, 245, 0.85)',  // 5. Pale Turquoise
        'rgba(255, 228, 181, 0.85)',  // 6. Moccasin
        'rgba(216, 191, 216, 0.85)',  // 7. Thistle
        'rgba(42, 192, 241, 0.85)',  // 8. Light Blue
        'rgba(252, 227, 0, 0.85)',  // 9. Pink
        'rgba(133, 133, 133, 0.85)',  // 10. Light Gray
        'rgba(207, 21, 1, 0.85)',  // 11. Misty Rose
        'rgba(119, 119, 255, 0.85)',  // 12. Lavender
        'rgba(255, 167, 25, 0.85)'   // 13. Papaya Whip
      ];

      // ✅ FIX: Assign unique color to each team
      const colors = teams.map((team, index) => pastelColors[index % 13]);

      console.log('  Colors generated:', colors.length);


      // Create horizontal bar chart
      window.overallChartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: teams,
          datasets: [{
            label: 'Points',
            data: points,
            backgroundColor: colors,
            borderColor: colors.map(color => color.replace('0.85', '1')),
            borderWidth: 2,
            borderRadius: 6,
            borderSkipped: false,
            // ✅ FIX: Better spacing to prevent overlap
            barThickness: 35,           // Slightly thinner (was 40)
            categoryPercentage: 0.85,   // More spacing between categories
            barPercentage: 0.75         // Bar takes 75% of category space
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
              backgroundColor: 'rgba(44, 26, 77, 0.95)',
              titleColor: '#F6C000',
              bodyColor: '#ffffff',
              borderColor: '#F6C000',
              borderWidth: 2,
              padding: 12,
              displayColors: true,
              callbacks: {
                label: function (context) {
                  return 'Points: ' + context.parsed.x;
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                stepSize: 5,
                font: { size: 12, weight: '600' },
                color: '#666'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              title: {
                display: true,
                text: 'Points',
                font: { size: 14, weight: 'bold' },
                color: '#5B2D8B'
              }
            },
            y: {
              ticks: {
                font: { size: 12, weight: '600' },
                color: '#2C1A4D'
              },
              grid: { display: false },
              title: {
                display: true,
                text: 'Teams',
                font: { size: 14, weight: 'bold' },
                color: '#5B2D8B'
              }
            }
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          animation: {
            duration: 1200,
            easing: 'easeInOutQuart'
          },
          layout: {
            padding: {
              left: 10,
              right: 10,
              top: 10,
              bottom: 10
            }
          }
        }
      });

      console.log('✅ Overall chart created successfully!');
      console.log('Chart instance:', window.overallChartInstance);
      console.log('=== OVERALL CHART RENDER END ===');
    } catch (error) {
      console.error('❌ Error creating overall chart:', error);
    }
  };

  const renderSportChart = () => {
    console.log('=== SPORT CHART RENDER START ===');
    console.log('Chart.js available?', typeof window.Chart !== 'undefined');
    console.log('Data:', sportLeaderboard);

    if (typeof window.Chart === 'undefined') {
      console.error('Chart.js not loaded yet for sport chart');
      return;
    }

    const ctx = document.getElementById('sportPointsChart');
    console.log('Canvas element:', ctx);

    if (!ctx) {
      console.warn('Sport chart canvas not yet available in DOM');
      return;
    }

    try {
      console.log('Creating sport chart...');

      // Destroy existing chart if it exists
      if (window.sportChartInstance) {
        console.log('Destroying previous sport chart instance');
        window.sportChartInstance.destroy();
      }

      // Prepare data
      const teams = sportLeaderboard.map(team => team.teamName || getTeamName(team.teamId));
      const points = sportLeaderboard.map(team => team.totalPoints || 0);

      console.log('Sport chart data prepared:');
      console.log('  Teams:', teams);
      console.log('  Points:', points);

      if (teams.length === 0 || points.length === 0) {
        console.error('No data to display in sport chart');
        return;
      }

      // Generate colors for each team
      const colors = [
        'rgba(246, 192, 0, 0.8)',    // Gold
        'rgba(91, 45, 139, 0.8)',    // Purple
        'rgba(48, 105, 181, 0.8)',   // Blue
        'rgba(40, 167, 69, 0.8)',    // Green
        'rgba(220, 53, 69, 0.8)',    // Red
        'rgba(255, 193, 7, 0.8)',    // Amber
        'rgba(156, 39, 176, 0.8)',   // Deep Purple
        'rgba(0, 188, 212, 0.8)',    // Cyan
        'rgba(255, 87, 34, 0.8)',    // Deep Orange
        'rgba(76, 175, 80, 0.8)',    // Light Green
        'rgba(233, 30, 99, 0.8)',    // Pink
        'rgba(103, 58, 183, 0.8)',   // Indigo
        'rgba(0, 150, 136, 0.8)'     // Teal
      ];

      console.log('  Colors selected:', colors.slice(0, teams.length).length);

      // Create chart
      window.sportChartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: teams,
          datasets: [{
            label: 'Points',
            data: points,
            backgroundColor: colors.slice(0, teams.length),
            borderColor: colors.slice(0, teams.length).map(color => color.replace('0.8', '1')),
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
            barPercentage: 0.6,  // Reduces bar width to 60% of available space
            categoryPercentage: 0.8  // Spacing between bars
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
              backgroundColor: 'rgba(44, 26, 77, 0.95)',
              titleColor: '#F6C000',
              bodyColor: '#ffffff',
              borderColor: '#F6C000',
              borderWidth: 2,
              padding: 12,
              displayColors: true,
              callbacks: {
                label: function (context) {
                  return 'Points: ' + context.parsed.y;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 5,
                font: { size: 12, weight: '600' },
                color: '#666'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              title: {
                display: true,
                text: 'Points',
                font: { size: 14, weight: 'bold' },
                color: '#5B2D8B'
              }
            },
            x: {
              ticks: {
                font: { size: 12, weight: '600' },
                color: '#2C1A4D',
                maxRotation: 45,
                minRotation: 0
              },
              grid: { display: false },
              title: {
                display: true,
                text: 'Teams',
                font: { size: 14, weight: 'bold' },
                color: '#5B2D8B'
              }
            }
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
          },
          layout: {
            padding: {
              left: 10,
              right: 10,
              top: 10,
              bottom: 10
            }
          }
        }
      });

      console.log('✅ Sport chart created successfully!');
      console.log('Chart instance:', window.sportChartInstance);
      console.log('=== SPORT CHART RENDER END ===');
    } catch (error) {
      console.error('❌ Error creating sport chart:', error);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    const token = getAuthToken();

    try {
      await Promise.all([
        fetchTeams(token),
        fetchAllSchedule(token),
        fetchOverallLeaderboard(token)
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async (token) => {
    try {
      const response = await fetch('https://api.asterickx.com/api/Teams', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
        if (data.length > 0) {
          setSelectedTeam(data[0].id); // Select first team by default
        }
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchAllSchedule = async (token) => {
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/standby-tournament/schedule/${TOURNAMENT_ID}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('All schedule raw data:', data);

        // ✅ FIX: Get current date at start of day for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // ✅ FIX: Filter only current and future matches
        const upcomingMatches = data.filter(match => {
          const matchDate = match.scheduledAt
            ? new Date(match.scheduledAt)
            : new Date(match.matchDate);
          matchDate.setHours(0, 0, 0, 0);
          return matchDate >= today;  // ✅ Only today and future
        });

        console.log('Filtered upcoming matches:', upcomingMatches.length);

        // Sort by date and time
        const sorted = upcomingMatches.sort((a, b) => {
          const dateA = a.scheduledAt ? new Date(a.scheduledAt) : new Date(a.matchDate + ' ' + a.matchTime);
          const dateB = b.scheduledAt ? new Date(b.scheduledAt) : new Date(b.matchDate + ' ' + b.matchTime);
          return dateA - dateB;
        });
        setAllSchedule(sorted);
      }
    } catch (error) {
      console.error('Error fetching all schedule:', error);
    }
  };

  const fetchTeamSchedule = async (teamId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/standby-tournament/team/${teamId}/schedule`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Team schedule raw data:', data);

        // ✅ FIX: Get current date at start of day for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // ✅ FIX: Filter only current and future matches
        const upcomingMatches = data.filter(match => {
          const matchDate = match.scheduledAt
            ? new Date(match.scheduledAt)
            : new Date(match.matchDate);
          matchDate.setHours(0, 0, 0, 0);
          return matchDate >= today;  // ✅ Only today and future
        });

        console.log('Filtered upcoming team matches:', upcomingMatches.length);

        // Sort by date and time
        const sorted = upcomingMatches.sort((a, b) => {
          const dateA = a.scheduledAt ? new Date(a.scheduledAt) : new Date(a.matchDate + ' ' + a.matchTime);
          const dateB = b.scheduledAt ? new Date(b.scheduledAt) : new Date(b.matchDate + ' ' + b.matchTime);
          return dateA - dateB;
        });
        setTeamSchedule(sorted);
      }
    } catch (error) {
      console.error('Error fetching team schedule:', error);
    }
  };

  const fetchOverallLeaderboard = async (token) => {
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/standby-tournament/leaderboard/${TOURNAMENT_ID}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (response.ok) {
        const data = await response.json();

        // ✅ Filter out "Bye" team
        const filteredData = data.filter(team => {
          const teamName = (team.teamName || '').toLowerCase();
          return teamName !== 'bye';
        });

        console.log('Filtered Bye team - Before:', data.length, 'After:', filteredData.length);
        setOverallLeaderboard(filteredData);
      }
    } catch (error) {
      console.error('Error fetching overall leaderboard:', error);
    }
  };

  const fetchSportLeaderboard = async (sportId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/standby-tournament/leaderboard/${TOURNAMENT_ID}/${sportId}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (response.ok) {
        const data = await response.json();
        setSportLeaderboard(data);
      }
    } catch (error) {
      console.error('Error fetching sport leaderboard:', error);
    }
  };

  const formatDateTime = (match) => {
    // Check if we have separate matchDate and matchTime fields
    if (match.matchDate && match.matchTime) {
      console.log('Using matchDate and matchTime:', match.matchDate, match.matchTime);

      // Parse date
      const dateObj = new Date(match.matchDate);
      const dateOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
      const formattedDate = dateObj.toLocaleDateString('en-US', dateOptions);

      // Use matchTime directly (assuming it's in format like "13:00:00" or "1:00 PM")
      let formattedTime = match.matchTime;

      // If matchTime is in 24-hour format (HH:mm:ss), convert to 12-hour
      if (match.matchTime.includes(':')) {
        const timeParts = match.matchTime.split(':');
        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12; // Convert to 12-hour format
        formattedTime = `${hours}:${minutes} ${ampm}`;
      }

      return {
        date: formattedDate,
        time: formattedTime
      };
    }

    // Fallback to scheduledAt if available
    if (match.scheduledAt) {
      console.log('Using scheduledAt:', match.scheduledAt);
      const date = new Date(match.scheduledAt);

      if (isNaN(date.getTime())) {
        console.log('Invalid scheduledAt date:', match.scheduledAt);
        return { date: 'Invalid Date', time: 'Invalid Date' };
      }

      const dateOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
      const timeOptions = { hour: '2-digit', minute: '2-digit' };
      return {
        date: date.toLocaleDateString('en-US', dateOptions),
        time: date.toLocaleTimeString('en-US', timeOptions)
      };
    }

    console.log('No date/time fields found in match:', match);
    return { date: 'Invalid Date', time: 'Invalid Date' };
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return '';
  };

  const getTeamName = (teamId) => {
    if (!teamId) {
      console.log('No teamId provided');
      return 'Unknown Team';
    }
    const team = teams.find(t => t.id === teamId);
    console.log('Finding team:', teamId, 'Result:', team);
    return team ? team.name : 'Unknown Team';
  };

  const getSportName = (sportId) => {
    if (!sportId) {
      console.log('No sportId provided');
      return 'Unknown Sport';
    }
    const sport = sports.find(s => s.id === sportId || s.id.toLowerCase() === sportId.toLowerCase());
    console.log('Finding sport:', sportId, 'Result:', sport);
    return sport ? sport.name : 'Unknown Sport';
  };

  if (loading) {
    return (
      <div className="tournament-dashboard">
        <div className="dashboard-header">
          <div className="header-logo-container">
            <img src={bishopGamesLogo} alt="Bishop Games" className="header-logo" />
          </div>
          <h1 className="dashboard-title">Bishop Games 6.0</h1>
          <p className="dashboard-subtitle">Tournament Dashboard</p>
        </div>
        <div className="loading-container">
          <div className="spinner-border text-primary"></div>
          <p className="loading-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tournament-dashboard">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button onClick={fetchInitialData} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-dashboard">
      {/* Header - Consistent with Match Scheduler */}
      <div className="dashboard-header">
        <button className="back-btn-dashboard" onClick={() => navigate(`/tournament-sports/${tournamentId}`)}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Sports
        </button>

        <div className="header-main-content-dash">
          {/* Left: Title Info */}
          <div className="header-left-content-dash">
            <h1 className="dashboard-title">
              <i className="fas fa-trophy me-2"></i>
              Bishop Games 6.0
            </h1>
            <p className="dashboard-subtitle">Tournament Dashboard</p>
          </div>

          {/* Center: Bishop Logo */}
          <div className="header-center-content-dash">
            <img src={bishopGamesLogo} alt="Bishop Games" className="header-logo" />
          </div>

          {/* Right: Empty for symmetry */}
          <div className="header-right-content-dash"></div>
        </div>
      </div>

      {/* 1. Stats Cards - 3 Cards Only */}
      <div className="stats-grid">
        <div className="stat-card stat-teams">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-number">
              {teams.filter(t => (t.name || '').toLowerCase() !== 'bye').length}
            </h3>
            <p className="stat-label">Teams</p>
          </div>
        </div>

        <div className="stat-card stat-players">
          <div className="stat-icon">
            <i className="fas fa-user-friends"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-number">503</h3>
            <p className="stat-label">Players</p>
          </div>
        </div>

        <div className="stat-card stat-sports">
          <div className="stat-icon">
            <i className="fas fa-gamepad"></i>
          </div>
          <div className="stat-content">
            <h3 className="stat-number">9</h3>
            <p className="stat-label">Sports</p>
          </div>
        </div>
      </div>

      {/* 2. Team Schedule */}
      <div className="section-container">
        <div className="section-header-left">
          <h2 className="section-title-left">
            <i className="fas fa-calendar-alt me-2"></i>
            TEAM SCHEDULE
          </h2>
          <div className="team-selector">
            <label htmlFor="team-select">
              <i className="fas fa-flag me-2"></i>
              Select Team:
            </label>
            <select
              id="team-select"
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="team-dropdown"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        </div>

        {teamSchedule.length > 0 ? (
          <div className="matches-grid">
            {teamSchedule.map((match, index) => {
              console.log('Team Schedule Match:', match);
              const { date, time } = formatDateTime(match);
              const sportName = getSportName(match.sportId);
              console.log('Formatted - Date:', date, 'Time:', time, 'Sport:', sportName);
              return (
                <div key={index} className="match-card">
                  <div className="match-sport-badge">{sportName}</div>
                  <div className="match-teams">
                    <div className={`team-box ${match.team1Id === selectedTeam ? 'selected-team' : ''}`}>
                      {getTeamName(match.team1Id)}
                    </div>
                    <div className="vs-badge">VS</div>
                    <div className={`team-box ${match.team2Id === selectedTeam ? 'selected-team' : ''}`}>
                      {getTeamName(match.team2Id)}
                    </div>
                  </div>
                  <div className="match-details">
                    <div className="match-info">
                      <i className="fas fa-calendar me-1"></i>
                      {date}
                    </div>
                    <div className="match-info">
                      <i className="fas fa-clock me-1"></i>
                      {time}
                    </div>
                  </div>
                  {match.venue && (
                    <div className="match-venue">
                      <i className="fas fa-map-marker-alt me-1"></i>
                      {match.venue}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data">
            <i className="fas fa-calendar-times"></i>
            <p>No scheduled matches for this team</p>
          </div>
        )}
      </div>

      {/* 3. Overall Leaderboard */}
      <div className="section-container">
        <h2 className="section-title-left">
          <i className="fas fa-trophy me-2"></i>
          OVERALL TOURNAMENT STANDINGS
        </h2>
        <p><i>*Excluding Chess Scores</i></p>

        {overallLeaderboard.length > 0 ? (
          <>
            {/* Interactive Horizontal Bar Chart */}
            <div className="chart-container">
              <div className="chart-header">
                <h3 className="chart-title">
                  <i className="fas fa-chart-bar me-2"></i>
                  Team vs Points
                </h3>
              </div>
              <div className="chart-wrapper chart-wrapper-horizontal">
                <canvas id="overallPointsChart"></canvas>
              </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Team</th>
                    <th>Played</th>
                    <th>Won</th>
                    <th>Lost</th>
                    <th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {overallLeaderboard.map((team, index) => {

                    // Calculate losses: Played - Won - Draws
                    const calculatedLoss = (team.gamesPlayed || 0) - (team.gamesWon || 0) - (team.draws || 0);
                    const displayLoss = team.loss !== undefined ? team.loss : calculatedLoss;

                    return (
                      <tr key={index} className={getRankClass(index + 1)}>
                        <td className="rank-cell">
                          {index === 0 && <span className="medal">🥇</span>}
                          {index === 1 && <span className="medal">🥈</span>}
                          {index === 2 && <span className="medal">🥉</span>}
                          {index > 2 && <span className="rank-number">{index + 1}</span>}
                        </td>
                        <td className="team-cell">{team.teamName || getTeamName(team.teamId)}</td>
                        <td className="played-cell">{team.gamesPlayed || 0}</td>
                        <td className="won-cell">{team.gamesWon || 0}</td>
                        <td className="lost-cell">{displayLoss}</td>
                        <td className="points-cell">{team.totalPoints || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="no-data">
            <i className="fas fa-chart-bar"></i>
            <p>No leaderboard data available</p>
          </div>
        )}
      </div>

      {/* 5. All Upcoming Matches */}
      <div className="section-container">
        <h2 className="section-title-left">
          <i className="fas fa-calendar-week me-2"></i>
          ALL UPCOMING MATCHES
        </h2>

        {allSchedule.length > 0 ? (
          <>
            {/* Scrollable table container */}
            <div className="scrollable-table-container">
              <table className="matches-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Sport</th>
                    <th>Team 1</th>
                    <th></th>
                    <th>Team 2</th>
                    <th>Date</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {allSchedule.map((match, index) => {
                    const { date, time } = formatDateTime(match);
                    const sportName = getSportName(match.sportId);
                    return (
                      <tr key={index}>
                        <td className="match-number-cell">{index + 1}</td>
                        <td className="sport-name-cell">
                          <span className="sport-badge-small">{sportName}</span>
                        </td>
                        <td className="team-name-cell">{getTeamName(match.team1Id)}</td>
                        <td className="vs-cell">VS</td>
                        <td className="team-name-cell">{getTeamName(match.team2Id)}</td>
                        <td className="date-cell">{date}</td>
                        <td className="time-cell">{time}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Scroll indicator */}
            {allSchedule.length > 10 && (
              <div className="scroll-indicator">
                <i className="fas fa-arrows-alt-v"></i>
                Scroll to view all {allSchedule.length} matches
              </div>
            )}
          </>
        ) : (
          <div className="no-data">
            <i className="fas fa-calendar-times"></i>
            <p>No upcoming matches scheduled</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentDashboard;